// src/lib/verify/normalize.ts
//
// Claimant-side normalisation for the citation verifier (Phase 12i).
// Each helper is single-purpose; the comparator composes them.

function stripDiacritics(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function unifyDashes(s: string): string {
  // em-dash (U+2014), en-dash (U+2013), minus (U+2212), figure-dash (U+2012),
  // horizontal bar (U+2015) — all collapsed to ASCII hyphen.
  return s.replace(/[‐‑‒–—―−]/g, "-");
}

/**
 * Collapse every typographic apostrophe to the ASCII one (U+0027).
 *
 * Same bug class as unifyDashes, on a different character, and found the same
 * way: registries store the curly right single quote (U+2019) where the citing
 * author typed an ASCII "'", so "O'Neill" and "O’Neill" survive normalisation as
 * different strings and only Jaro-Winkler's tolerance absorbs the difference —
 * which is LENGTH-DEPENDENT, so it holds for a long surname and fails for a
 * short one. Measured 2026-08-02 on the n=300 co-author corpus: `O'Hern` and
 * `O'Neill` both produced confident false accusations against records that
 * carried the identical name in curly form, on two independent draws.
 *
 * Covers the right/left single quotes (U+2019/U+2018), the modifier letter
 * apostrophe (U+02BC — the correct character for Irish and transliterated
 * Semitic names, and what a careful cataloguer uses), the modifier prime
 * (U+02B9), the prime (U+2032), and the acute/grave accents that appear when a
 * publisher's pipeline mangles an apostrophe. NFKC does NOT fold any of these —
 * they are distinct characters, not compatibility forms — so this must be
 * explicit.
 *
 * Affects Irish (O'Brien, O'Neill), Italian (D'Amelio, Dell'Acqua), French
 * (D'Arcy), Arabic/Hebrew transliterations (Ma'ayan, Sa'adi), and Hawaiian
 * ('okina). Widely enough distributed that the previous behaviour was a
 * systematic false-accusation source for one class of surname.
 *
 * ⛔ MUST RUN BEFORE NFKC. NFKC rewrites the acute accent (U+00B4) and grave
 * (U+0060) into COMBINING marks, which stripDiacritics then deletes outright —
 * so "D´Arcy" would silently become "darcy" and stop matching "D'Arcy" rather
 * than starting to. Running first, while they are still standalone characters,
 * is what makes those two variants reachable at all.
 */
function unifyApostrophes(s: string): string {
  return s.replace(/[’‘ʼʹ′´`]/g, "'");
}

/**
 * Remove HTML markup from a string — both tags (`<i>`, `</i>`,
 * `<tag attr="…">`) and the most common entity references (`&amp;`,
 * `&lt;`, `&#39;`, `&#x2014;`). NUMERIC entities (`&#947;`, `&#x3b3;`) are
 * DECODED to their character so an entity-encoded Greek letter or accent
 * flows into the NFKC + transliteration steps downstream (a resolved title
 * carrying `IFN-&#947;` must still match a claimed `IFN-gamma`). NAMED
 * entities (`&amp;`, `&nbsp;`) are replaced with a single space rather than
 * decoded — the downstream normalisation collapses whitespace and strips
 * punctuation anyway, so the result is equivalent to a plain-text claim.
 *
 * Upstream CSL / Crossref / DataCite responses preserve italic markup in
 * title fields for catalog references (Gaia, Hubble), genus species
 * (*E. coli*), and mathematical variables; container titles like
 * "Astronomy &amp; Astrophysics" preserve the ampersand entity.
 * Without stripping, the verifier's normalizeTitle leaks the tag letters
 * ("i", "em", "sup") and entity names ("amp") as spurious tokens that
 * drag similarity below the matched threshold — the live regression
 * captured 2026-05-13 with ADS bibcode 2019A&A...625A.135L
 * (Lallement et al.) where `<i>Gaia</i>-2MASS …` scored 0.82 against the
 * plain claim.
 *
 * The angle-bracket entities `&lt;`/`&gt;` are DECODED to real brackets
 * first, so ENTITY-encoded tags survive the tag strip below. Some
 * publishers (e.g. Dove Press) wrap titles as `&lt;p&gt;…&lt;/p&gt;`;
 * without decoding, the brackets collapse to spaces and the tag-name
 * letter ("p") leaks as a spurious token (live regression 2026-06-05 with
 * DOI 10.2147/cia.s244707, which scored 0.84 against the plain claim).
 *
 * Conservative-by-design: the tag pattern requires `<` followed by an
 * ASCII letter (or `/` then a letter), so decoded mathematical notation
 * like `<5` or `a>b` stays intact (the trailing entity strip + downstream
 * punctuation strip then drop the lone bracket either way).
 */
// Decode a numeric character-reference codepoint to its string. Guards against
// out-of-range and surrogate codepoints (a malformed entity falls back to a
// space, matching the old strip-to-space behaviour).
function decodeCodePoint(cp: number): string {
  // Out-of-range or lone-surrogate codepoint → space (matches the old
  // strip-to-space). Callers always pass an integer (Number(decimal) /
  // parseInt(hex)), and this range is exactly String.fromCodePoint's valid
  // domain, so the decode below cannot throw.
  if (cp <= 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return " ";
  return String.fromCodePoint(cp);
}

export function stripHtmlMarkup(s: string): string {
  if (!s) return "";
  return (
    s
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      // Decode numeric entities to their character BEFORE the tag strip, so an
      // entity-encoded Greek letter / accent survives into NFKC + transliteration
      // (and an entity-encoded tag, e.g. `&#60;i&#62;`, is removed by the strip).
      .replace(/&#(\d+);/g, (_m, n) => decodeCodePoint(Number(n)))
      .replace(/&#x([0-9a-fA-F]+);/g, (_m, h) => decodeCodePoint(parseInt(h, 16)))
      .replace(/<\/?[a-zA-Z][^>]*>/g, "")
      // Remaining NAMED entities (`&amp;`, `&nbsp;`) → space.
      .replace(/&[a-zA-Z]+;/g, " ")
  );
}

/**
 * Strip publisher-style retraction/withdrawal/correction prefixes from
 * resolved titles before similarity comparison.
 *
 * Crossref, PubMed, and Springer prepend strings like "RETRACTED: ",
 * "Retraction Note to: ", "WITHDRAWN: ", "Expression of Concern: ", or
 * "Retraction of: " to the title field of retracted / corrected papers.
 * The claimed citation almost never carries the prefix — a clinician
 * pasting Wakefield's Lancet 1998 paper writes the original title, not
 * "RETRACTED: Ileal-lymphoid-nodular hyperplasia…".
 *
 * Without stripping, the raw similarity drops below the matched threshold
 * (~0.81 in the live Wakefield 1998 case, captured 2026-05-18 via the
 * Obsidian plugin verifier modal) and the verifier emits `ambiguous`
 * instead of `matched`. The matched-record display still shows the raw
 * resolved title so users see the retraction signal — only the similarity
 * comparison ignores the prefix.
 *
 * Prefix shapes captured from live Crossref / PubMed metadata:
 *   "RETRACTED: ..."
 *   "RETRACTED ARTICLE: ..."
 *   "Retraction Note: ..."
 *   "Retraction Note to: ..."
 *   "Retraction: ..."
 *   "Retraction of: ..."
 *   "WITHDRAWN: ..."
 *   "Expression of Concern: ..."
 *   "Correction: ..." / "Corrigendum: ..."
 *
 * Match is anchored at the start of the string, case-insensitive, and
 * requires a trailing colon followed by whitespace — narrow enough to
 * avoid stripping legitimate prose like "Retraction is rare in chemistry".
 */
const RETRACTION_PREFIX_RE =
  /^(?:retracted(?:\s+article)?|retraction(?:\s+note(?:\s+to)?|\s+of)?|withdrawn|expression\s+of\s+concern|correction|corrigendum)\s*:\s+/i;

export function stripRetractionPrefix(s: string): string {
  if (!s) return "";
  return s.replace(RETRACTION_PREFIX_RE, "");
}

/**
 * Standard Latin romanisation of the Greek alphabet (α→alpha, β→beta,
 * γ→gamma, …). The range /[α-ω]/ covers every lowercase Greek letter,
 * including the final sigma ς (U+03C2), so the map is exhaustive for the
 * input transliterateGreek() sees.
 */
const GREEK_TRANSLITERATION: Record<string, string> = {
  α: "alpha",
  β: "beta",
  γ: "gamma",
  δ: "delta",
  ε: "epsilon",
  ζ: "zeta",
  η: "eta",
  θ: "theta",
  ι: "iota",
  κ: "kappa",
  λ: "lambda",
  μ: "mu",
  ν: "nu",
  ξ: "xi",
  ο: "omicron",
  π: "pi",
  ρ: "rho",
  σ: "sigma",
  ς: "sigma",
  τ: "tau",
  υ: "upsilon",
  φ: "phi",
  χ: "chi",
  ψ: "psi",
  ω: "omega",
};

/**
 * Latin-extended letters that NFD does NOT decompose (they are atomic letters,
 * not letter+combining-mark) and so survive stripDiacritics untouched — folded
 * here to their conventional ASCII romanisation. Without this, "Sørensen",
 * "Łukasiewicz", or an "Œuvre"/"Æsthetics" title leaks a non-ASCII letter that
 * drags Jaro-Winkler below the match threshold (worst when the letter sits in a
 * leading, prefix-weighted word). Keys are lowercase only — normalizeTitle
 * case-folds before transliterating, so Ø/Æ/Ł already arrive as ø/æ/ł.
 */
const LATIN_TRANSLITERATION: Record<string, string> = {
  ø: "o",
  æ: "ae",
  œ: "oe",
  ł: "l",
  đ: "d",
  ð: "d",
  þ: "th",
  ħ: "h",
  ı: "i",
  ŋ: "ng",
  ß: "ss",
  ſ: "s",
  ĸ: "k",
};
const LATIN_RE = new RegExp(`[${Object.keys(LATIN_TRANSLITERATION).join("")}]`, "g");

/**
 * Fold Greek and Latin-extended letters to their standard ASCII romanisation.
 * Citing authors routinely spell out a Greek letter the canonical title carries
 * as the symbol — "Calcineurin Aalpha" vs "Calcineurin Aα", "IFN-gamma" vs
 * "IFN-γ" — and Latin-extended letters (ø, ł, æ, œ, ð, þ, ß) that NFD cannot
 * decompose otherwise survive as lone non-ASCII tokens. Either drags
 * Jaro-Winkler below the matched threshold (Greek known-hard surfaced 2026-06-05
 * with the Calcineurin "Aα"/"Aalpha" case; the Latin-extended gap surfaced in
 * the 2026-06-05 normalisation coverage sweep).
 *
 * Recall-safe by the same argument as stripDiacritics: it folds a letter to its
 * romanisation, which can only RAISE similarity when one side spelled it out. A
 * fabricated or unrelated title cannot be lifted over the matched threshold by
 * folding a letter.
 */
function transliterate(s: string): string {
  // The /[α-ω]/ range (U+03B1–U+03C9) is exactly the 25 keys of
  // GREEK_TRANSLITERATION, and LATIN_RE is built from LATIN_TRANSLITERATION's
  // keys, so both lookups are total — no fallback branch.
  return s
    .replace(/[α-ω]/g, (ch) => GREEK_TRANSLITERATION[ch])
    .replace(LATIN_RE, (ch) => LATIN_TRANSLITERATION[ch]);
}

/**
 * Leading definite/indefinite articles across the languages the ISBN arm
 * actually draws from. `normalizeTitle` has always stripped a leading English
 * "the"; this is the same rule extended, and it exists because BOOK catalogues
 * disagree with citations about the article far more often than journals do
 * ("La Monadologie" cited, "Monadologie" catalogued — similarity 0.823, a
 * confident false accusation on the same work).
 *
 * ⚠️ This accepts a known, deliberate collision: two works differing ONLY by
 * their leading article now compare equal. That is not a new risk class — the
 * shipped "the" strip already accepts exactly that for English — and no pair of
 * real books distinguished solely by "A" vs "The" appears in any corpus we
 * measure. The alternative (false-accusing every article disagreement) is the
 * strictly worse trade: measured at 9 of 42 ISBN rows in the v5 holdout.
 */
const LEADING_ARTICLE_RE =
  /^(the|a|an|la|le|les|el|los|las|il|lo|der|die|das|den|det|een|het|un|una|uma)(\s+)/;

/**
 * Trailing edition / format / bundled-material statements that a catalogue
 * appends but a citation omits ("The Norton Shakespeare" vs "The Norton
 * Shakespeare, Based on the Oxford Edition, Second Edition" — 0.859).
 *
 * ⛔ `volume`/`vol` are deliberately NOT in this list. A volume number is the
 * discriminating part of the title ("Capital, Volume I" vs "Volume III"), so
 * stripping it would collapse genuinely different works onto each other. Same
 * reasoning for edition NUMBERS: this pattern only fires when an edition WORD is
 * present, and callers must compare the stripped form ADDITIVELY (via max), so a
 * pair that already matched cannot be made worse.
 */
const EDITION_TAIL_RE =
  /[,;([]\s*[^,;()[\]]*\b(edition|edn|reprint|printing|impression|revised|deluxe|classics|student|handbook|insert|dvd|paperback|hardcover|illustrated)\b[^()[\]]*[)\]]?\s*$/i;

/**
 * Strip repeated trailing edition/format statements. Repeated because
 * catalogues stack them ("… (Classics Deluxe Edition) (Penguin Classics Deluxe
 * Edition)"). Returns the input unchanged when nothing matches.
 */
export function stripEditionTail(s: string): string {
  if (!s) return "";
  let out = s;
  let prev: string;
  do {
    prev = out;
    out = out.replace(EDITION_TAIL_RE, "").replace(/[\s,;:.-]+$/, "");
  } while (out !== prev && out.length > 0);
  return out || s;
}

/**
 * `normalizeTitle` with every leading article removed (repeatedly — "the a" does
 * not occur, but the loop costs nothing and avoids an ordering assumption).
 * Separate from `normalizeTitle` so callers opt in via max(), keeping the change
 * additive and recall-safe.
 */
export function normalizeTitleNoArticle(s: string): string {
  let out = normalizeTitle(s);
  let prev: string;
  do {
    prev = out;
    out = out.replace(LEADING_ARTICLE_RE, "");
  } while (out !== prev);
  return out;
}

/**
 * `normalizeTitle` with ALL whitespace removed. Repairs catalogue records that
 * have stripped a diacritic into a SPACE rather than to its base letter
 * ("Mediterráneo" stored as "Mediterra neo" — 0.914, a confident false
 * accusation). Diacritic folding cannot fix that, because the damage is a word
 * boundary, not an accent.
 */
export function normalizeTitleNoSpace(s: string): string {
  return normalizeTitle(s).replace(/\s+/g, "");
}

export function normalizeTitle(s: string): string {
  if (!s) return "";
  // Strip HTML tags first so embedded <i>, <em>, <sup> markup doesn't
  // leak the tag-name letters into the normalised token stream.
  const noTags = stripHtmlMarkup(s);
  // NFKC handles compatibility forms (ligatures, full-width chars).
  // Strip diacritics after NFKC so accented letters fold cleanly.
  const base = stripDiacritics(unifyDashes(noTags.normalize("NFKC")).toLowerCase());
  // Fold Greek + Latin-extended letters to their ASCII romanisation AFTER
  // case-fold + diacritic strip, so only bare lowercase forms (γ not Γ/ά;
  // ø not Ø) remain to map. See transliterate() for the recall-safety argument.
  const romanised = transliterate(base);
  // Keep letters, digits, hyphens. Everything else becomes whitespace.
  // Hyphen is preserved so compound words match cleanly.
  const stripped = romanised
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Drop a leading "the" — citations commonly include or omit the article
  // ("The Lord of the Rings" vs "Lord of the Rings"). The trailing branch
  // handles a degenerate bare "The".
  return stripped.replace(/^the(\s+|$)/, "");
}

// unifyDashes is applied here for the same reason it is applied in
// normalizeTitle/normalizeContainer: registries store a Unicode hyphen (U+2010)
// or en-dash where the citing author typed an ASCII "-". Without it the
// substitution survives normalisation and only Jaro-Winkler's tolerance absorbs
// it — which is length-dependent, so it holds for a long surname and FAILS for a
// short one. Measured: "Duarte-Cabral" scored 0.969 (matched) while "Wu-Li",
// "Ng-Yu", "Ho-Sun" and "Lo-Fu" scored 0.893-0.911, under the 0.92 threshold,
// producing verdict `mismatch` — a confident false accusation on a purely
// cosmetic difference, concentrated in short Chinese/Korean/Vietnamese surnames.
// Same length-sensitivity bug class as the Sørensen/Dieckhoff split documented in
// compare.ts, and the same false-accusation shape as the hyphen fix in 4790d80e.
// unifyApostrophes is applied for exactly the same reason and is needed ONLY on
// this path: normalizeTitle/normalizeContainer strip all punctuation to spaces,
// so a curly apostrophe is already neutralised there, but an author surname
// KEEPS its punctuation (it is part of the name) and so carries the difference
// straight into the comparison. See unifyApostrophes() for the measurement.
export function normalizeAuthorFamily(s: string): string {
  if (!s) return "";
  // ⛔ transliterate() folds Latin-extended letters (ł→l, ø→o, ß→ss, đ→d) that
  // NFD CANNOT decompose — they are atomic letters, not letter+combining-mark,
  // so stripDiacritics leaves them untouched. normalizeTitle has always applied
  // it; this path did NOT, which meant a surname carrying one of those letters
  // never matched its ASCII spelling. Measured 2026-08-03 on the co-author
  // corpus: "Miłoś" vs "Milos" produced a false accusation, and a sweep found
  // 5 of 7 real surnames affected — Polish (Łukasiewicz), Danish/Norwegian
  // (Sørensen — named in compare.ts's own comments as a known split), German
  // (Strauß), Serbian/Croatian (Đokić). Accented forms that NFD *can* decompose
  // (Halldórsson, Ingólfsson) already worked, which is why the gap survived:
  // it looks like diacritic handling and is not.
  return transliterate(
    stripDiacritics(unifyDashes(unifyApostrophes(s).normalize("NFKC")).toLowerCase()),
  )
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeContainer(s: string): string {
  if (!s) return "";
  const noTags = stripHtmlMarkup(s);
  const base = stripDiacritics(unifyDashes(noTags.normalize("NFKC")).toLowerCase());
  const stripped = base
    .replace(/[^\p{L}\p{N}\s-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Drop a leading "the" (followed by whitespace or end-of-string) — common
  // journal-name prefix that adds no signal. The end-of-string branch
  // handles degenerate inputs like a bare "The ".
  return stripped.replace(/^the(\s+|$)/, "");
}

export function subtitleSplit(s: string): { full: string; head: string } {
  const idx = s.indexOf(":");
  if (idx === -1) return { full: s, head: s };
  return { full: s, head: s.slice(0, idx).trim() };
}
